<template>
  <BaseModal
    no-padding
    no-exit
    card-width="480px"
  >
    <template #default>
      <div class="flex flex-col items-center justify-center w-full h-full gap-3 pt-1">
        <div
          class="rounded-md p-2 border-border-normal w-11 h-11"
          style="border-width: 1.5px;"
        >
          <div
            class="bg-secondary-yellow-10 rounded-md p-1 w-full h-full flex items-center justify-center"
          >
            <ConnectionIcon
              class="text-secondary-yellow-200"
              :width="18"
              :height="18"
            />
          </div>
        </div>
        <div class="space-y-3 text-center px-7">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Your Meta account has disconnected.
          </BaseText>
          <BaseText
            size="sm"
            class="text-text-normal"
          >
            We lost connection to your Meta account. Please re-authenticate your ad account.
          </BaseText>
        </div>
        <div class="flex flex-col gap-2.5 w-full px-5 pb-5">
          <BaseText
            type="label"
            size="xs"
            class="text-text-muted"
          >
            Connection Created by:
          </BaseText>
          <div class="bg-background-normal rounded-md w-full flex items-center gap-2 p-3">
            <img
              v-if="status.connectionOwner?.avatar"
              class="rounded-full w-5 h-5"
              :src="status.connectionOwner?.avatar"
            >
            <img
              v-else
              src="../../assets/images/defaultProfileImage.webp"
              class="rounded-full w-5 h-5"
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted flex-1"
            >
              {{ status.connectionOwner?.name }}
            </BaseText>
            <BaseText
              size="sm"
              class="text-text-normal"
            >
              {{ status.connectionOwner?.email }}
            </BaseText>
          </div>
        </div>
        <!-- Footer -->
        <div class="p-5 w-full flex items-center justify-between border-t border-border-normal">
          <button
            class="px-3 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25 h-8"
            @click="$emit('close')"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Close
            </BaseText>
          </button>
          <button
            class="px-3 py-1.5 rounded-md black-btn-primary h-8 w-40 flex items-center justify-center relative group"
            :disabled="!isConnectionOwner || loading"
            @click="handleRetry"
          >
            <div
              v-if="!isConnectionOwner"
              class="btn-tooltip text-neutral-alpha-650 group-hover:opacity-100 opacity-0 transition-opacity pointer-events-none"
            >
              <BaseText
                size="sm"
                class="p-2 text-left"
              >
                The original account auth {{ status.connectionOwner?.email ?? 'creator' }} needs to restore the connection.
              </BaseText>
            </div>
            <BaseLoadingSpinnerCircle
              v-if="loading"
              class="text-text-muted"
            />
            <BaseText
              v-else
              type="label"
              size="sm"
              :class="isConnectionOwner ? 'text-white' : 'text-text-disabled'"
            >
              Restore Connection
            </BaseText>
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters } from 'vuex'
import LensAPI from '@/api/lens'
// Icons
import ConnectionIcon from '../globals/Icons/ConnectionIcon.vue'

export default {
  name: 'LensConnectionLostModal',
  components: {
    ConnectionIcon
  },
  props: {
    status: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      twAccountId: null,
      url: null,
      loading: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    isConnectionOwner () {
      return this.status.connectionOwner?.user_id === this.getUser.user_id
    }
  },
  methods: {
    async handleRetry () {
      if (!this.isConnectionOwner) {
        this.$showAlert({
          message: 'You are not the connection owner.',
          type: 'error'
        })
        return
      }
      try {
        this.loading = true

        // Check if a connection exists first
        const accountExists = await LensAPI.Lens.getTWAccounts()

        // If we have accounts, use redirect flow regardless of is_valid status
        if (accountExists.data && accountExists.data.length > 0) {
          // Use the internal_tw_account_id from the response for redirect
          const twAccountId = accountExists.data[0]?.internal_tw_account_id
          const { url } = await LensAPI.Lens.getTWRedirect(twAccountId)
          this.url = url
          this.twAccountId = twAccountId
        }

        // Open authentication window in a new tab
        this.openAuthWindow()
      } catch (error) {
        console.error('Failed to get Meta connection URL:', error)
        this.$showAlert({
          message: 'Failed to connect to Meta',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    openAuthWindow () {
      // Calculate center position for the window
      const width = 600
      const height = 600
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2

      // Open new window centered on screen
      try {
        this.authWindow = window.open(
          this.url,
          'MetaAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        )
      } catch (error) {
        console.error('Error opening auth window:', error)
      }
    }
  }
}
</script>

<style scoped>
.btn-tooltip {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(6, 7, 15, 0.92);
  padding: 5px 10px;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 220px;
}
</style>
